import { useState, useEffect } from 'react';
import type { FC } from 'react';
import { Box, Select, Grid, FormControl, CircularProgress, Radio, RadioGroup, FormControlLabel, Button, MenuItem, Backdrop } from '@mui/material';
import { useParams } from 'react-router-dom';
import reportapi from '../../api/ReportApi';
import { IReportDefinition } from '../../types/reportDefinition';
import TextBoxFilterField from '../../components/report/TextBoxFilterField';
import DropdownFilterField from '../../components/report/DropdownFilterField';
import DateRangeFilterField from '../../components/report/DateRangeFilterField';
import { IReportField } from '../..//types/reportField';
import ConfirmationDialog from '../../components/controls/ConfirmationDialog';
import useAuth from '../../hooks/useAuth';
import PageContainerLayout from '../PageContainerLayout';
import moment from 'moment';

const Reports: FC<React.PropsWithChildren<unknown>> = () => {
  const { customerNumber } = useParams();
  const [categories, setCategories] = useState<any[]>([]);
  const [category, setCategory] = useState<string>('');
  const [reportName, setReportName] = useState<string>('');
  const [reports, setReports] = useState<any[]>([]);
  const [report, setReport] = useState<IReportDefinition>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [reportFormat, setReportFormat] = useState<string>('excel');
  const [message, setMessage] = useState<string>('');
  const [messageTitle, setMessageTitle] = useState<string>('');
  const [reportConfirmationOpen, setReportConfirmationOpen] = useState<boolean>(false);

  const { user } = useAuth();

  const loadCategories = async () => {
    setIsLoading(true);
    let cat: any[] = await reportapi.getReportCategories();
    setCategories(cat);
    setIsLoading(false);
  };

  const handleCategoryChanged = async (e) => {
    const newCategory = e.target.value;
    setCategory(newCategory);
    setIsLoading(true);
    setReportName('');
    setReport(null);

    if (!newCategory) {
      setReports([]);
      setIsLoading(false);
      return;
    }

    let rpt: any[] = await reportapi.getReportList(newCategory);
    setReports(rpt);
    setReport(null);
    setIsLoading(false);
  };

  const handleReportChanged = async (e) => {
    setReportName(e.target.value);
    setReport(null);
    setIsLoading(true);

    if (!e.target.value) {
      setReports([]);
      setReport(null);
      setIsLoading(false);
      return;
    }

    let rpt: IReportDefinition = await reportapi.getReportDefinition(+user.id, customerNumber, e.target.value);
    rpt?.reportFields?.map((rf) => {
      if (rf.fieldType === 3) {
        rf.fieldValue = moment().subtract(1, 'years').format('YYYY-MM-DD');
        rf.fieldValue2 = moment().format('YYYY-MM-DD');
      }
    });
    setReport(rpt);
    setIsLoading(false);
  };

  const filterValueUpdated = async (reportField: IReportField, value: string | null, forSecondValue: boolean = false) => {
    report.reportFields.map((f) => {
      if (f.name === reportField.name && value !== null) {
        if (f.fieldType === 3) {
          if (forSecondValue) {
            f.fieldValue2 = value;
          } else {
            f.fieldValue = value;
          }
        } else {
          f.fieldValue = value;
        }
      }
    });
  };

  const runReport = async () => {
    if (!report) {
      return;
    }

    setIsLoading(true);

    // Don't want to send options as could be a lot of data, rebuild with just what we need.
    //const reportParam = { description, selectedReport, reportFields } as IReportDefinition;
    const reportParam = report;

    let reportDoc = await reportapi.getReport(customerNumber, reportParam, reportFormat === 'excel' ? true : false);
    setIsLoading(true);

    if (reportDoc.longRunning) {
      setCategories([]);
      setReports([]);
      setReport(null);
      setReportFormat('excel');
      setReportConfirmationOpen(true);
      setMessageTitle('Long Running Report...');
      setMessage('Your report is running and will be emailed to you when finished. You may run another report now if you wish, or continue browsing the site.');
      setIsLoading(false);
      await loadCategories();
      return;
    } else if (reportDoc.message) {
      setReportConfirmationOpen(true);
      setMessageTitle('Error');
      setMessage('An error occurred. Please change your criteria and try again.');
      setIsLoading(false);
      return;
    } else if (reportDoc.noRecords) {
      setReportConfirmationOpen(true);
      setMessageTitle('No Records');
      setMessage('No records were returned. Please change your criteria and try again.');
      setIsLoading(false);
    } else {
      let title = reports.find((i) => i.key === reportName).text;
      let today = new Date();
      let date: string = `${today.getMonth() + 1}-${today.getDate()}-${today.getFullYear()}`;
      let a = document.createElement('a');
      a.download = `${title}_${date}`;
      a.href = `data:application/${reportFormat === 'excel' ? 'vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'pdf'};base64,${reportDoc.reportData}`;
      a.click();
    }

    setIsLoading(false);
  };

  useEffect(() => {
    loadCategories();
  }, []);

  return (
    <PageContainerLayout pageHeaderTitle="Reports" hasDivider>
      {message && (
        <ConfirmationDialog
          title={messageTitle}
          open={reportConfirmationOpen}
          cancelHidden={true}
          onClose={(confirmed) => {
            setReportConfirmationOpen(false);
            if (!confirmed) {
              return;
            }
          }}
        >
          {message}
        </ConfirmationDialog>
      )}
      <Box sx={{ pt: 2 }}>
        <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <Grid container>
          <Grid size={{ xs: false, sm: 2, md: 4 }}></Grid>
          <Grid size={{ xs: 12, sm: 8, md: 4 }}>
            <Grid container>
              <Grid size={{ xs: 12 }}>
                <FormControl sx={{ mb: 3, width: '100%' }}>
                  <Box sx={{ mb: 1 }}>
                    <label>Report Category:</label>
                  </Box>
                  <Select
                    id="categorySelect"
                    value={category ?? ''}
                    onChange={(value) => handleCategoryChanged(value)}
                    size="small"
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return '(None)';
                      }
                      const cat = categories.find((c) => c.value?.toString() === selected);
                      return cat ? cat.text : selected;
                    }}
                  >
                    {[
                      <MenuItem key={-1} value="">
                        (None)
                      </MenuItem>,
                    ].concat(
                      categories?.map((c, i) => (
                        <MenuItem key={i} value={c && c.value.toString() ? c.value.toString() : null}>
                          {c.text}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControl sx={{ mb: 3, width: '100%' }}>
                  <Box sx={{ mb: 1 }}>
                    <label>Report:</label>
                  </Box>
                  <Select
                    id="reportSelect"
                    value={reportName ?? ''}
                    onChange={(value) => handleReportChanged(value)}
                    size="small"
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return '(None)';
                      }
                      const report = reports.find((c) => c.value?.toString() === selected);
                      return report ? report.text : selected;
                    }}
                  >
                    {[
                      <MenuItem key={-1} value="">
                        (None)
                      </MenuItem>,
                    ].concat(
                      reports?.map((r, i) => (
                        <MenuItem key={i} value={r && (r.value as any).toString()}>
                          {r.text}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                {(report &&
                  report.reportFields?.map((rf, i) =>
                    rf.fieldType === 1 ? (
                      <TextBoxFilterField key={i} reportField={rf} onChange={(e) => filterValueUpdated(rf, e.target.value)} />
                    ) : rf.fieldType === 2 ? (
                      <DropdownFilterField key={i} reportField={rf} onChange={(e) => filterValueUpdated(rf, e.target.value)} />
                    ) : rf.fieldType === 3 ? (
                      <DateRangeFilterField
                        key={i}
                        reportField={rf}
                        onChangeSecondValue={(date) => filterValueUpdated(rf, date ? date.target.value : null, true)}
                        onChange={(date) => filterValueUpdated(rf, date ? date.target.value : null, false)}
                      />
                    ) : null
                  )) ||
                  null}
              </Grid>
              {report && !report.useExcel && (
                <Grid size={{ xs: 12 }}>
                  <FormControl sx={{ m: 1, width: '100%' }}>
                    <label>Format</label>
                    <RadioGroup name="format-radio-buttons-group" row>
                      <FormControlLabel
                        control={<Radio />}
                        label="Excel"
                        value="excel"
                        checked={reportFormat === 'excel'}
                        name="reportFormat"
                        onChange={() => setReportFormat('excel')}
                      />
                      <FormControlLabel control={<Radio />} label="PDF" value="pdf" checked={reportFormat === 'pdf'} name="reportFormat" onChange={() => setReportFormat('pdf')} />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              )}
              {report && !isLoading && (
                <Grid size={{ xs: 12 }}>
                  <FormControl sx={{ m: 1 }}>
                    <Button onClick={() => runReport()} variant="outlined">
                      Run Report
                    </Button>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid size={{ xs: false, sm: 2, md: 4 }}></Grid>
        </Grid>
      </Box>
    </PageContainerLayout>
  );
};

export default Reports;
